import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';

import RightModalLayout from "../layouts/Modal/RightModalLayout";
import FormProviderMC from "../components/FormProviderMC";
import InputComponent from "../components/InputMC";
import DropdownMC from "../components/DropdownMC";
import { getCountryOptions } from "../../services/countryService";
import CustomersModelMc from '../models/CustomersModelMc';
import { SIDEBAR_ACTIONS } from "../../constants/actionsConstants";

/**
 * Checks if a customer with the given phone number or email already exists.
 *
 * @param {Object} customer - The customer object.
 * @param {string} [customer.phone] - The phone number of the customer.
 * @param {string} [customer.email] - The email address of the customer.
 * @returns {Promise<boolean>} - A promise that resolves to true if a customer with the given phone number or email exists, otherwise false.
 */
const existsPhone = async (customer) => {
  try {
    const textSearch = customer.phone && customer.phone.length > 0 ? `${customer.phone.replace(/^(0+)/g, '')}` : customer.email;
    const response = await CustomersModelMc.search({ text: textSearch });
    return response?.customers?.length > 0;
  } catch (error) {
    console.error(error);
  }
  return false;
};

/**
 * CreateCustomer component for creating a new customer.
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onCreateSuccess - Callback function to be called when customer creation is successful.
 * @returns {JSX.Element} The rendered CreateCustomer component.
 *
 * @example
 * <CreateCustomer onCreateSuccess={handleCreateSuccess} />
 */
const CreateCustomer = ({ onCreateSuccess }) => {
  const { t } = useTranslation('Funnels');
  const dispatch = useDispatch();
  const [optionsCountries, setOptionsCountries] = useState([]);

  const schema = object().shape({
    first_name: string().required(t('deal.customer.modal.first_name.required')),
    last_name: string().required(t('deal.customer.modal.last_name.required')),
    country_id: string().required(t('deal.customer.modal.country.required')),
    phone: string()
      .nullable()
      .when('email', {
        is: (email) => !email || email.trim() === '',
        then: (schema) =>
          schema
            .required(t('deal.customer.modal.phone.required'))
            .matches(/^\d+$/, t('deal.customer.modal.phone.validation_format')),
        otherwise: (schema) => schema.matches(/^\d*$/, t('deal.customer.modal.phone.validation_format')),
      }),
    email: string()
      .nullable()
      .test(
        'is-valid-email',
        t('deal.customer.modal.email.validation_format'),
        (value) => !value || (typeof value === 'string' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      ),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      first_name: '',
      last_name: '',
      phone: '',
      email: '',
      country_id: ENV.COUNTRY_CODE,
    },
  });

  const submitAction = async (data) => {
    if (await existsPhone(data)) {
      alert('Este cliente ya existe. Lo puede buscar en la lista de selección del formulario inicial.');
      return;
    }

    onCreateSuccess(data);
    closeSidebar();
  };

  useEffect(() => {
    setOptionsCountries(getCountryOptions().map((country) => ({
      id: country.code,
      ...country,
    })));
  }, []);

  const filterOption = (searchTerm) => {
    return optionsCountries.filter((option) => option.name_es?.toLowerCase().includes(searchTerm.toLowerCase()) || option.name_en?.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  const closeSidebar = () => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: {
        componentKey: 'CreateCustomer'
      }
    });
  };

  return (
    <RightModalLayout
      title={t('deal.customer.modal.title')}
      submitAction={methods.handleSubmit(submitAction)}
      cancelAction={closeSidebar}
      closeAction={closeSidebar}
      submitLabel={t('deal.customer.modal.add')}
      cancelLabel={t('deal.customer.modal.cancel')}
      footerVariant="HORIZONTAL"
      footerClassName="tw-border-none"
    >
      <FormProviderMC methods={methods}>
        <InputComponent
          useFloatingLabel
          name="first_name"
          label={t('deal.customer.modal.first_name.title')}
          placeholder={t('deal.customer.modal.first_name.placeholder')}
          className="tw-mb-4"
        />
        <InputComponent
          useFloatingLabel
          name="last_name"
          label={t('deal.customer.modal.last_name.title')}
          placeholder={t('deal.customer.modal.last_name.placeholder')}
          className="tw-mb-4"
        />
        <div className="tw-grid tw-grid-cols-12 tw-mb-4">
          <DropdownMC
            search
            useFloatingLabel
            name="country_id"
            label={t('deal.customer.modal.country.title')}
            defaultValue={ENV.COUNTRY_CODE}
            options={optionsCountries}
            filterOption={filterOption}
            placeholder={t('deal.customer.modal.country.placeholder')}
            className="tw-col-span-3"
          />
          <div className="tw-col-span-9 tw-ml-2">
            <InputComponent
              useFloatingLabel
              name="phone"
              label={t('deal.customer.modal.phone.title')}
              placeholder={t('deal.customer.modal.phone.placeholder')}
            />
          </div>
        </div>
        <InputComponent
          useFloatingLabel
          name="email"
          label={t('deal.customer.modal.email.title')}
          placeholder={t('deal.customer.modal.email.placeholder')}
          className="tw-mb-4"
        />
      </FormProviderMC>
    </RightModalLayout>
  );
};

export default CreateCustomer;